import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { apiClient } from '@/lib/api-client';
import { IdentityProvider, type MeResponse } from '@/lib/identity-context';
import { FullPageSpinner } from '@/components/full-page-spinner';
import { AuthErrorPage } from '@/components/auth-error-page';
import { routeTree } from './router';
import './index.css';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Router is constructed with an empty identity context; the provider
// supplies the real MeResponse once /me resolves, and the router reads
// from it via TanStack Router's `context` helper in leaf routes.
// Note: defaultPreload: 'intent' was disabled while chasing a
// continuous forced-reflow loop that caused click events to drop on
// the site detail page. If it's not the cause, this can be re-enabled
// later — preloading is a UX polish, not a functional requirement.
const router = createRouter({
  routeTree,
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function IdentityBootstrap() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MeResponse> => {
      const { data: me, error: err } = await apiClient.GET('/me');
      if (err) throw err;
      return me;
    },
  });
  if (isLoading) return <FullPageSpinner label="Signing in…" />;
  if (error || !data) return <AuthErrorPage error={error} />;
  return (
    <IdentityProvider value={data}>
      <RouterProvider router={router} />
    </IdentityProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IdentityBootstrap />
    </QueryClientProvider>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');
createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
